import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { addDays, format, parseISO } from 'date-fns'
import { ArrowRight, CalendarDays, Camera, Check, Compass, Phone, ShoppingBag } from 'lucide-react'
import { shopSetupProgress, track } from '@adthlete/shared-frontend'
import type { AppDispatch } from '@/store'
import { updateUser } from '@/redux/userSlice'
import {
  useGetLabCallQuery,
  useGetMyMarketplaceProfileQuery,
  useGetPlannedPostsQuery,
} from '@/redux/collaborationsApi'
import StrategyCallPicker from '@/components/lab/StrategyCallPicker'
import { useAuth } from '@/hooks/useAuth'
import { useCollaborations } from '@/hooks/useCollaborations'
import { themesOf } from '@/lib/athleteScore'
import athleteApi from '@/lib/api'
import { cn } from '@/lib/utils'

const ymd = (d: Date) => format(d, 'yyyy-MM-dd')
const day = (d: string) => format(parseISO(d), 'EEE, MMM d')
const money = (n?: number | null) => (n && n > 0 ? `$${Math.round(n).toLocaleString()}` : null)

const Step: React.FC<{
  n: number
  icon: React.ElementType
  title: string
  done?: boolean
  children: React.ReactNode
}> = ({ n, icon: Icon, title, done, children }) => (
  <li className="flex gap-3">
    <span
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
        done ? 'bg-[#C0FD72] text-[#0F172A]' : 'bg-muted text-foreground',
      )}
      aria-label={done ? `Step ${n}, done` : `Step ${n}`}
    >
      {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
    </span>
    <div className="min-w-0 flex-1 pb-1">
      <p className="font-bold text-foreground">{title}</p>
      <div className="mt-0.5 text-sm text-gray-600">{children}</div>
    </div>
  </li>
)

const linkClass = 'mt-1.5 inline-flex items-center gap-1 text-sm font-semibold text-[#4d7c0f]'

/**
 * What a new athlete sees once they're in (after the phone check, a password, and their school,
 * date of birth and the terms). No quests and no score: any offer waiting for them comes first.
 *
 * At a Creator Lab school (schools.data.creator_lab), then how their first month works with the
 * Lab: they book their strategy call right here, the Lab films with them on campus and plans the
 * month on their content calendar, and builds their Content Shop for them to approve. Each step
 * ticks off as it happens. Anywhere else: how to get booked on their own.
 *
 * Opening it marks the welcome seen, so the whole app is open from here on.
 */
const Welcome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { athlete } = useAuth()
  const { bids, refetch: refetchDeals } = useCollaborations()
  // A Creator Lab welcome offer is sent the moment sign-up finishes, after the deals may have
  // been loaded: load them again so it's here
  useEffect(() => {
    refetchDeals()
  }, [refetchDeals])
  const firstName = athlete?.name?.split(' ')[0] ?? ''
  const today = new Date()
  const { data: planned } = useGetPlannedPostsQuery({
    from: ymd(today),
    to: ymd(addDays(today, 35)),
  })
  const { data: shop } = useGetMyMarketplaceProfileQuery()
  const { data: labCall } = useGetLabCallQuery()
  const lab = labCall?.lab ?? !!athlete?.school?.creatorLab

  const seen = !!athlete?.welcome_completed
  useEffect(() => {
    if (seen) return
    dispatch(updateUser({ welcome_completed: true }))
    athleteApi.post('/athlete/complete-welcome').catch(() => {
      // Non-fatal: the next visit to Home shows this screen again
    })
  }, [seen, dispatch])

  const posts = (planned?.posts ?? []).filter((p) => p.status !== 'skipped' && p.plannedOn)
  const shoots = [...new Set(posts.map((p) => p.shootOn).filter(Boolean) as string[])].sort()
  const strategySet =
    labCall?.call?.status === 'done' || themesOf((athlete ?? {}) as any).length > 0
  const shopOpen = !!shop && shopSetupProgress(shop).ready

  return (
    <div className="mx-auto max-w-lg space-y-5 pb-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {firstName ? `Welcome to Athios, ${firstName}` : 'Welcome to Athios'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {bids.length
            ? 'A brand is waiting on you. Take a look, then here is how your first month works.'
            : lab
              ? 'Here is how your first month works. The Athios Lab does it with you.'
              : 'Here is how brands find you and book you.'}
        </p>
      </div>

      {/* Offers first: they're why most athletes are here */}
      {bids.map((offer) => (
        <Link
          key={offer.id}
          to={`/deals/${offer.id}`}
          onClick={() => track('welcome_open_offer', { dealId: offer.id })}
          className="flex items-center gap-3 rounded-2xl border border-[#C0FD72] bg-[#C0FD72]/15 p-4"
        >
          {offer.organization?.logo ? (
            <img
              src={offer.organization.logo}
              alt=""
              className="h-11 w-11 shrink-0 rounded-xl bg-card object-contain"
            />
          ) : (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-card font-bold text-foreground">
              {(offer.brand?.name ?? '?').charAt(0)}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-bold uppercase tracking-wider text-[#4d7c0f]">
              Offer waiting
            </span>
            <span className="block truncate font-bold text-foreground">
              {offer.brand?.name ?? 'A brand'}
              {money(offer.paymentAmount) ? ` · ${money(offer.paymentAmount)}` : ''}
            </span>
            <span className="block truncate text-sm text-gray-600">{offer.title}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-foreground">
            Open <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      ))}

      {/* The first month with the Lab, at a Lab school */}
      {lab ? (
      <section className="rounded-2xl border border-foreground/10 bg-card p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
        <h2 className="text-lg font-bold text-foreground">Your first month with the Athios Lab</h2>
        <p className="mt-0.5 text-sm text-gray-600">
          We plan it, film it and post it with you. You show up and be yourself.
        </p>
        <ol className="mt-4 space-y-4">
          <Step n={1} icon={Phone} title="Strategy call" done={strategySet}>
            {strategySet ? (
              'Done. Your content themes are set.'
            ) : (
              <>
                Fifteen minutes with the Lab: what you want to post, and the brands you want to
                work with.
                {labCall ? <StrategyCallPicker state={labCall} /> : null}
              </>
            )}
          </Step>
          <Step n={2} icon={Camera} title="Shoot days on campus" done={shoots.length > 0}>
            {shoots.length
              ? `${shoots.length} on your calendar. The first is ${day(shoots[0])}.`
              : 'The Lab films with you. Your shoot days go on your calendar once your strategy is set.'}
          </Step>
          <Step n={3} icon={CalendarDays} title="Your first month of content" done={posts.length > 0}>
            {posts.length ? (
              <>
                <ul className="mt-1 space-y-1">
                  {posts.slice(0, 3).map((p) => (
                    <li key={p.id} className="flex gap-2">
                      <span className="w-24 shrink-0 text-gray-500">{day(p.plannedOn!)}</span>
                      <span className="min-w-0 truncate text-foreground">
                        {p.hook || p.idea || p.theme?.label || 'Post'}
                      </span>
                    </li>
                  ))}
                </ul>
                {posts.length > 3 ? (
                  <span className="mt-1 block">and {posts.length - 3} more.</span>
                ) : null}
              </>
            ) : (
              'Every post planned for you: the idea, the shot list and the day it goes up.'
            )}
            <Link to="/calendar" className={cn(linkClass, 'flex')}>
              Open your calendar <ArrowRight className="h-4 w-4" />
            </Link>
          </Step>
          <Step n={4} icon={ShoppingBag} title="Your Content Shop" done={shopOpen}>
            {shopOpen
              ? 'Open. Brands can book your content.'
              : 'The Lab builds it from your content so brands can book you. You look it over and open it with one tap.'}
            <Link to="/brand-view" className={cn(linkClass, 'flex')}>
              See your shop <ArrowRight className="h-4 w-4" />
            </Link>
          </Step>
        </ol>
      </section>
      ) : (
        <section className="rounded-2xl border border-foreground/10 bg-card p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
          <h2 className="text-lg font-bold text-foreground">Get booked</h2>
          <ol className="mt-4 space-y-4">
            <Step n={1} icon={ShoppingBag} title="Your Content Shop" done={shopOpen}>
              {shopOpen
                ? 'Open. Brands can book your content.'
                : 'The content you make and your prices, so brands can book you in a tap.'}
              <Link to="/brand-view" className={cn(linkClass, 'flex')}>
                {shopOpen ? 'See your shop' : 'Set it up'} <ArrowRight className="h-4 w-4" />
              </Link>
            </Step>
            <Step n={2} icon={CalendarDays} title="Your content calendar" done={posts.length > 0}>
              Two posts a week keeps your content consistent, and brands notice.
              <Link to="/calendar" className={cn(linkClass, 'flex')}>
                Plan your posts <ArrowRight className="h-4 w-4" />
              </Link>
            </Step>
            <Step n={3} icon={Compass} title="Opportunities">
              Brands looking for athletes like you. Apply in a tap.
              <Link to="/opportunities" className={cn(linkClass, 'flex')}>
                See what's open <ArrowRight className="h-4 w-4" />
              </Link>
            </Step>
          </ol>
        </section>
      )}

      <Link
        to="/"
        className="flex h-12 w-full items-center justify-center rounded-xl bg-[#C0FD72] text-base font-bold text-[#0F172A] hover:bg-[#d2ff9c]"
      >
        Go to Home
      </Link>
    </div>
  )
}

export default Welcome
